import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, type MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthorization } from '$/components/core/Authentication/hooks/useAuthorization';
import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import { LoadingMaterialCard } from '$/components/core/Collection/LoadingMaterialCard';
import { MaterialCard } from '$/components/core/Collection/MaterialCard';
import { SectionHeadline } from '$/components/core/Collection/SectionHeadline';
import { config } from '$/config';
import { DashboardSectionRow } from '$/pages/DashboardPages/components/DashboardSectionRow';
import { materialsQuery } from '$/services/usecases/materials/queries';
import { repeat } from '$/utils/arrayUtils';

export const CollectionSection = () => {
  const { t } = useTranslation();

  const { hasPermission } = useAuthorization();
  const openAuthenticationModal = useAuthenticationStore.useOnModalOpen();

  const { data, isLoading } = useQuery(
    materialsQuery({
      ids: config.materials.homepagePreviewIds,
      isDemo: true,
    }),
  );

  const materials = useMemo(() => {
    if (!data) return null;
    const allMaterials = data.results;
    return {
      colorMaterial: allMaterials.find((material) =>
        material?.type?.includes('color_tint'),
      ),
      facadeMaterial: allMaterials.find((material) =>
        material?.type?.includes('facade_texture'),
      ),
      floorMaterial: allMaterials.find((material) =>
        material?.type?.includes('floor_texture'),
      ),
      wallMaterial: allMaterials.find((material) =>
        material?.type?.includes('wall_texture'),
      ),
    };
  }, [data]);

  const isDemoUser = !hasPermission('View_All_Collections');

  const handleLinkClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
    if (isDemoUser) {
      openAuthenticationModal('versionComparison');
      e.preventDefault();
    }
  };

  return (
    <SectionHeadline
      headline={t('dashboard.welcome.collectionsText')}
      showAllUrl='/collections'
    >
      <DashboardSectionRow>
        {isLoading && repeat(4).map((i) => <LoadingMaterialCard key={i} />)}

        {materials && (
          <>
            {materials.colorMaterial && (
              <Link to='/collections/colors' key={materials.colorMaterial.id}>
                <MaterialCard
                  key={materials.colorMaterial.id}
                  material={materials.colorMaterial}
                  labelOverwrite={t('dashboard.welcome.collection.colorTones')}
                  editMode='none'
                  cursor='pointer'
                  disableBrightness={true}
                />
              </Link>
            )}

            {materials.wallMaterial && (
              <Link
                to='/collections/walls'
                key={materials.wallMaterial.id}
                onClick={handleLinkClick}
              >
                <MaterialCard
                  key={materials.wallMaterial.id}
                  material={materials.wallMaterial}
                  labelOverwrite={t(
                    'dashboard.welcome.collection.wallCoverings',
                  )}
                  editMode='none'
                  cursor='pointer'
                  disableBrightness={true}
                />
              </Link>
            )}

            {materials.floorMaterial && (
              <Link
                to='/collections/floors'
                key={materials.floorMaterial.id}
                disabled={isDemoUser}
                onClick={handleLinkClick}
              >
                <MaterialCard
                  material={materials.floorMaterial}
                  key={materials.floorMaterial.id}
                  labelOverwrite={t(
                    'dashboard.welcome.collection.floorCoverings',
                  )}
                  editMode='none'
                  cursor='pointer'
                  disableBrightness={true}
                />
              </Link>
            )}

            {materials.facadeMaterial && (
              <Link
                to='/collections/facades'
                key={materials.facadeMaterial.id}
                disabled={isDemoUser}
                onClick={handleLinkClick}
              >
                <MaterialCard
                  key={materials.facadeMaterial.id}
                  material={materials.facadeMaterial}
                  labelOverwrite={t('dashboard.welcome.collection.facades')}
                  editMode='none'
                  cursor='pointer'
                  disableBrightness={true}
                />
              </Link>
            )}
          </>
        )}
      </DashboardSectionRow>
    </SectionHeadline>
  );
};
